<script setup lang="ts">
import { computed } from "vue";
import Spinner from "../Spinner.vue";

type Props = {
  loading?: boolean;
  disabled?: boolean;
  color?: "blue" | "red" | "yellow";
  percentage?: number | null;
  fullWidth?: boolean;
  size?: "normal" | "small";
};

type Emits = {
  (e: "click", event: MouseEvent): void;
};

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  disabled: false,
  color: "blue",
  percentage: null,
  fullWidth: false,
  size: "normal",
});

const emit = defineEmits<Emits>();

const buttonClasses = computed(() => {
  const baseClasses: string[] = [
    "relative",
    "flex",
    "items-center",
    "justify-center",
    "gap-2",
    "rounded-lg",
    "border-2",
    "border-black-dark",
    "font-bold",
    "text-gray-900",
    "transition-all",
    "hover:brightness-110",
    "active:translate-x-[2px]",
    "active:translate-y-[2px]",
    "active:shadow-none",
  ];

  // Size-specific classes
  if (props.size === "small") {
    baseClasses.push("px-3", "py-1.5", "text-sm");
  } else {
    baseClasses.push("px-4", "py-2");
  }

  if (props.fullWidth) {
    baseClasses.push("w-full");
  } else {
    baseClasses.push("mx-auto");
  }

  // Shadow and background by color
  const colorStyles: Record<string, string[]> = {
    blue: ["bg-kekkle-blue-light", "shadow-blue-hard-sm"],
    red: ["bg-kekkle-red-light", "shadow-red-hard-sm"],
    yellow: ["bg-kekkle-yellow", "shadow-yellow-hard-sm"],
  };

  const disabledClasses: string[] = props.disabled
    ? [
        "cursor-not-allowed",
        "opacity-50",
        "bg-gray-400",
        "text-gray-600",
        "shadow-none",
      ]
    : colorStyles[props.color] || [];

  return [...baseClasses, ...disabledClasses];
});

function handleClick(event: MouseEvent): void {
  if (!props.disabled && !props.loading) {
    emit("click", event);
  }
}
</script>

<template>
  <button
    :class="buttonClasses"
    :disabled="disabled || loading"
    type="button"
    @click="handleClick"
  >
    <div
      v-if="loading"
      class="absolute top-0 left-0 flex h-full w-full items-center justify-center"
    >
      <p v-if="percentage" class="mr-2 font-medium">
        {{ Math.floor(percentage) }}%
      </p>
      <Spinner class="w-4 animate-spin" />
    </div>
    <div :class="{ invisible: loading }">
      <slot></slot>
    </div>
  </button>
</template>
